import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { UserPlus, Upload, Send, Loader2, FileDown } from "lucide-react";

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contacts");
      setContacts(data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 4000); };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/contacts/upload", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      flash(`Inserted ${data.inserted} · duplicates ${data.duplicates} · invalid ${data.invalid}`);
      await load();
    } catch (err) { flash(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const sendInvites = async () => {
    if (!window.confirm(`Send invites to ${contacts.filter(c => !c.invited).length} pending contact(s)?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/contacts/send-invites");
      flash(`Sent ${data.sent} invite(s)`);
      await load();
    } catch (err) { flash(formatApiError(err.response?.data?.detail) || "Send failed"); }
    finally { setSending(false); }
  };

  const downloadSample = () => {
    const blob = new Blob(["phone,name\n+254712345678,Jane Doe\n+254798765432,John Mwangi\n"],
      { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "mydawa_contacts_sample.csv";
    a.click();
  };

  const pending = contacts.filter((c) => !c.invited).length;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-[#1a1c19] sm:text-3xl flex items-center gap-2">
            <UserPlus className="h-6 w-6 text-[#66cc33]" /> Contacts
          </h1>
          <p className="text-sm text-[#5c665a]">
            Upload a CSV of existing customers/leads. Each contact becomes an invitable referrer with a unique link. Send invites in bulk from here.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={downloadSample} className="admin-btn-ghost" data-testid="contacts-sample">
            <FileDown className="h-4 w-4" /> Sample CSV
          </button>
          <label className="admin-btn cursor-pointer">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? "Uploading…" : "Upload CSV"}
            <input type="file" accept=".csv" className="hidden" ref={fileRef}
                   onChange={upload} disabled={uploading} data-testid="contacts-upload" />
          </label>
          <button
            onClick={sendInvites}
            disabled={sending || pending === 0}
            className="admin-btn"
            data-testid="contacts-send-invites"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send {pending} invite{pending === 1 ? "" : "s"}
          </button>
        </div>
      </div>

      {toast && <div className="rounded-lg bg-[#eaf8de] px-4 py-2 text-sm font-semibold text-[#3f7c21]" data-testid="contacts-toast">{toast}</div>}

      <div className="admin-card overflow-x-auto p-0">
        <table className="min-w-full text-sm" data-testid="contacts-table">
          <thead className="bg-[#fafcfa] text-left text-xs uppercase tracking-wider text-[#5c665a]">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Phone</th>
              <th className="px-4 py-3">Invited</th>
              <th className="px-4 py-3">Uploaded</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#e2e8e2]">
            {loading ? (
              <tr><td colSpan={4} className="px-4 py-6 text-center"><Loader2 className="inline h-4 w-4 animate-spin" /></td></tr>
            ) : contacts.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-xs text-[#5c665a]">No contacts yet. Upload a CSV to begin.</td></tr>
            ) : contacts.map((c) => (
              <tr key={c.id}>
                <td className="px-4 py-2 font-semibold text-[#1a1c19]">{c.name || "—"}</td>
                <td className="px-4 py-2 font-mono text-xs">{c.phone}</td>
                <td className="px-4 py-2">
                  {c.invited ? (
                    <span className="inline-flex items-center rounded-full bg-[#eaf8de] px-2.5 py-1 text-[11px] font-bold text-[#3f7c21]">
                      sent {c.invited_at ? new Date(c.invited_at).toLocaleDateString() : ""}
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-[#fafcfa] px-2.5 py-1 text-[11px] font-bold text-[#5c665a]">pending</span>
                  )}
                </td>
                <td className="px-4 py-2 text-xs text-[#5c665a]">{new Date(c.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
